import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { BaseChartColors, BaseFontColors } from "../constants";
import { BarSize } from "./types";

const LINE_SERIES_COLOR = "#e0f2f1";

interface LegendItem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickHandler: (ev: any) => void;
}

export const createCategoryAxis = (chart: am4charts.XYChart) => {
  const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = "category";
  categoryAxis.renderer.grid.template.location = 0;

  categoryAxis.cursorTooltipEnabled = false;

  categoryAxis.renderer.labels.template.disabled = false;
  categoryAxis.renderer.minGridDistance = 1;

  categoryAxis.renderer.labels.template.tooltipText = "{category}";
  categoryAxis.renderer.labels.template.maxWidth = 200;
  categoryAxis.renderer.labels.template.truncate = true;
  categoryAxis.renderer.labels.template.tooltip = new am4core.Tooltip();
  categoryAxis.renderer.labels.template.tooltip.fontSize = 11;
  categoryAxis.renderer.labels.template.tooltip.fill = am4core.color(
    BaseFontColors.TextDark
  );

  categoryAxis.renderer.labels.template.fill = am4core.color(
    BaseFontColors.TextDark
  );
  categoryAxis.renderer.labels.template.fontSize = 11;

  categoryAxis.renderer.labels.template.paddingRight = 17;

  categoryAxis.renderer.grid.template.adapter.add("disabled", (_, target) => {
    if (target.dataItem) {
      return target.dataItem.index !== 0;
    }
    return false;
  });
};

export const createBarAxis = (chart: am4charts.XYChart, xAxisLabel: string) => {
  const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());

  valueAxis.cursorTooltipEnabled = false;

  valueAxis.strictMinMax = true;
  valueAxis.startLocation = 0;
  valueAxis.endLocation = 1;
  valueAxis.renderer.minGridDistance = 90;

  valueAxis.min = 0;

  valueAxis.renderer.labels.template.fontSize = 11;
  valueAxis.renderer.labels.template.fill = am4core.color(
    BaseFontColors.TextDark
  );

  valueAxis.renderer.labels.template.dy = -5;

  valueAxis.title.text = xAxisLabel;
  valueAxis.title.fontSize = 11;
  valueAxis.title.fill = am4core.color(BaseFontColors.TextDark);

  valueAxis.title.dy = -10;
  valueAxis.title.dx = 0;

  chart.maskBullets = false;

  return valueAxis;
};

export const createLineAxis = (
  chart: am4charts.XYChart,
  barAxis: am4charts.ValueAxis<am4charts.AxisRenderer>
) => {
  const lineAxis = chart.xAxes.push(new am4charts.ValueAxis());
  lineAxis.cursorTooltipEnabled = false;
  lineAxis.renderer.opposite = true;
  lineAxis.renderer.grid.template.disabled = true;
  lineAxis.renderer.labels.template.disabled = true;
  lineAxis.syncWithAxis = barAxis;

  return lineAxis;
};

export const createBarSeries = (
  chart: am4charts.XYChart,
  baseColor: BaseChartColors,
  barSeriesName: string,
  barSize?: "small" | "medium" | "large"
) => {
  const series = chart.series.push(new am4charts.ColumnSeries());
  series.dataFields.categoryY = "category";
  series.dataFields.valueX = "barSeriesValue";
  series.columns.template.strokeOpacity = 0;

  // Adjust the height of the bars (vertical dimension) based on barSize prop
  if (barSize === BarSize.SMALL) {
    series.columns.template.height = am4core.percent(15);
  } else if (barSize === BarSize.MEDIUM) {
    series.columns.template.height = am4core.percent(40);
  }
  const defaultFillColor = am4core.color(baseColor);
  series.columns.template.fill = defaultFillColor;
  series.name = barSeriesName;
  series.fill = am4core.color(baseColor);
  series.tooltipText = `${barSeriesName}: {barSeriesValue}`;

  if (series.tooltip) {
    series.tooltip.label.fontSize = 10;
    series.tooltip.label.fontWeight = "bold";
  }

  series.columns.template.adapter.add("fill", function (_fill, target) {
    if (target.dataItem) {
      const totalBars = chart.data.length;
      const { index } = target.dataItem;

      if (index === totalBars - 1) {
        return defaultFillColor;
      } else if (index % 2 !== 0) {
        const pattern = new am4core.LinePattern();
        pattern.rotation = -45;

        const lightenFactor = 0.15 + 0.08 * (totalBars - index - 1);

        const lighterColor = defaultFillColor.lighten(lightenFactor);
        pattern.stroke = defaultFillColor.lighten(lightenFactor * 0.5);
        pattern.strokeWidth = 2;
        pattern.width = 12;
        pattern.height = 12;
        pattern.backgroundFill = lighterColor;
        pattern.backgroundOpacity = 1;
        return pattern;
      } else {
        const lightenFactor = 0.15 + 0.08 * (totalBars - index - 1);
        return defaultFillColor.lighten(lightenFactor);
      }
    }
    return defaultFillColor;
  });

  const labelBullet = series.bullets.push(new am4charts.LabelBullet());
  labelBullet.label.text = "{barSeriesValue}";
  labelBullet.label.dx = 22;
  labelBullet.label.truncate = false;
  labelBullet.label.fontSize = 12;
  labelBullet.label.fontWeight = "bold";
};

export const createLineSeries = (
  chart: am4charts.XYChart,
  lineSeriesName: string,
  lineAxis?: am4charts.ValueAxis
) => {
  const lineSeries = chart.series.push(new am4charts.LineSeries());
  lineSeries.dataFields.categoryY = "category";
  lineSeries.dataFields.valueX = "lineSeriesValue";

  lineSeries.strokeWidth = 3;
  lineSeries.stroke = am4core.color(LINE_SERIES_COLOR);
  lineSeries.name = lineSeriesName;
  lineSeries.fill = am4core.color(LINE_SERIES_COLOR);
  lineSeries.tooltipText = `${lineSeriesName}: {lineSeriesValue}`;

  if (lineAxis) {
    lineSeries.xAxis = lineAxis;
  }

  if (lineSeries.tooltip) {
    lineSeries.tooltip.label.fontSize = 10;
    lineSeries.tooltip.label.fontWeight = "bold";
  }

  const circleBullet = lineSeries.bullets.push(new am4charts.CircleBullet());
  circleBullet.circle.fill = am4core.color(LINE_SERIES_COLOR);
  circleBullet.circle.strokeWidth = 0.5;
  circleBullet.circle.stroke = am4core.color(LINE_SERIES_COLOR);
};

export const createLegend = (chart: am4charts.XYChart) => {
  chart.legend = new am4charts.Legend();
  chart.legend.position = "bottom";
  chart.legend.align = "right";
  chart.legend.contentAlign = "right";
  chart.legend.dx = 75;

  chart.legend.fontSize = 12;
  chart.legend.fontWeight = "500";
  chart.legend.labels.template.fill = am4core.color(BaseFontColors.TextDark);
  chart.legend.useDefaultMarker = true;
  chart.legend.markers.template.width = 16;
  chart.legend.markers.template.height = 4;

  const lineSeries = chart.series.getIndex(1);
  const columnSeries = chart.series.getIndex(0) as am4charts.ColumnSeries;

  if (lineSeries && columnSeries) {
    chart.legend.data = [
      {
        clickHandler: (ev) => {
          const { target } = ev;
          lineSeries.visible = !lineSeries.visible;
          target.opacity = lineSeries.visible ? 1 : 0.3;
          if (target.label) {
            target.label.opacity = lineSeries.visible ? 1 : 0.3;
          }
        },
      } as LegendItem,
      {
        clickHandler: (ev) => {
          const { target } = ev;
          columnSeries.visible = !columnSeries.visible;
          target.opacity = columnSeries.visible ? 1 : 0.3;
          if (target.label) {
            target.label.opacity = columnSeries.visible ? 1 : 0.3;
          }
        },
      } as LegendItem,
    ];

    chart.legend.itemContainers.template.events.on("hit", (ev) => {
      const item = ev.target.dataItem?.dataContext as LegendItem;
      if (item.clickHandler) {
        item.clickHandler(ev);
      }
    });
    chart.legend.itemContainers.template.cursorOverStyle =
      am4core.MouseCursorStyle.pointer;
  }
};
